import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminProgrammeDetail,
  ListResponse,
  ProgrammeInput,
  ProgrammeTranslationInput,
  PublicProgrammeListItem,
  PublicProgrammeSlot,
  SlotInput,
} from '../types';
import { api } from './client';
import { qk } from './keys';

export interface ProgrammesListParams {
  locale: string;
  page?: number;
  pageSize?: number;
}

export function usePublicProgrammes(params: ProgrammesListParams) {
  return useQuery({
    queryKey: qk.programmes({ ...params, scope: 'public' }),
    queryFn: () => api.get<ListResponse<PublicProgrammeListItem>>('/programmes', params),
  });
}

/** Admin list — includes inactive programmes. Use this on admin-only screens. */
export function useAdminProgrammes(params: ProgrammesListParams) {
  return useQuery({
    queryKey: qk.programmes({ ...params, scope: 'admin' }),
    queryFn: () => api.get<ListResponse<PublicProgrammeListItem>>('/admin/programmes', params),
  });
}

/** Admin programme detail — includes the per-locale translations array. */
export function useProgramme(slug: string | null, locale: string) {
  return useQuery({
    queryKey: qk.programme(slug ?? '', `${locale}|admin`),
    enabled: !!slug,
    queryFn: () =>
      api.get<AdminProgrammeDetail>(`/admin/programmes/${encodeURIComponent(slug ?? '')}`, {
        locale,
      }),
  });
}

export function useCreateProgramme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgrammeInput) =>
      api.post<{ id: string; slug: string }>('/admin/programmes', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['programmes'] }),
  });
}

/**
 * After any programme mutation, invalidate **both** the list query (key
 * starts with `['programmes']`) and every detail query (key starts with
 * `['programme', …]`). React Query's `invalidateQueries` matches by prefix,
 * and the singular vs. plural keys live in separate cache trees — so
 * invalidating only the list (the previous behaviour) left the detail page
 * showing pre-mutation values until a full page refresh.
 */
function invalidateAllProgrammeQueries(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['programmes'] });
  void qc.invalidateQueries({ queryKey: ['programme'] });
}

export function useUpdateProgramme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: Partial<ProgrammeInput> }) =>
      api.patch(`/admin/programmes/${args.id}`, args.input),
    onSuccess: () => invalidateAllProgrammeQueries(qc),
  });
}

export function useDeleteProgramme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/programmes/${id}`),
    onSuccess: () => invalidateAllProgrammeQueries(qc),
  });
}

export function useUpsertProgrammeTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; locale: string; input: ProgrammeTranslationInput }) =>
      api.put(`/admin/programmes/${args.id}/translations/${args.locale}`, args.input),
    onSuccess: () => invalidateAllProgrammeQueries(qc),
  });
}

export function useDeleteProgrammeTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; locale: string }) =>
      api.delete(`/admin/programmes/${args.id}/translations/${args.locale}`),
    onSuccess: () => invalidateAllProgrammeQueries(qc),
  });
}

export function useAddSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { programmeId: string; input: SlotInput }) =>
      api.post<{ id: string }>(`/admin/programmes/${args.programmeId}/slots`, args.input),
    onSuccess: () => {
      // The detail panel's slots table reads from `useProgramme(slug, locale)`
      // (key `['programme', …]`) — invalidating only `['programmes']` plural
      // left the table stale until a hard refresh. Hit both roots, plus the
      // schedule day / week caches feeding the FullCalendar view.
      invalidateAllProgrammeQueries(qc);
      void qc.invalidateQueries({ queryKey: ['schedule-day'] });
      void qc.invalidateQueries({ queryKey: ['schedule-week'] });
    },
  });
}

export function useUpdateSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { programmeId: string; slotId: string; input: Partial<SlotInput> }) =>
      api.patch(`/admin/programmes/${args.programmeId}/slots/${args.slotId}`, args.input),
    onSuccess: () => {
      invalidateAllProgrammeQueries(qc);
      void qc.invalidateQueries({ queryKey: ['schedule-day'] });
      void qc.invalidateQueries({ queryKey: ['schedule-week'] });
    },
  });
}

export function useDeleteSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { programmeId: string; slotId: string }) =>
      api.delete(`/admin/programmes/${args.programmeId}/slots/${args.slotId}`),
    onSuccess: () => {
      invalidateAllProgrammeQueries(qc);
      void qc.invalidateQueries({ queryKey: ['schedule-day'] });
      void qc.invalidateQueries({ queryKey: ['schedule-week'] });
    },
  });
}

export type { PublicProgrammeSlot };

export function useBulkDeleteSlots() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ programmeId, ids }: { programmeId: string; ids: string[] }) =>
      api.post<{ deleted: number }>(`/admin/programmes/${programmeId}/slots/bulk-delete`, { ids }),
    onSuccess: () => {
      invalidateAllProgrammeQueries(qc);
      void qc.invalidateQueries({ queryKey: ['schedule-day'] });
      void qc.invalidateQueries({ queryKey: ['schedule-week'] });
    },
  });
}
